// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import './LicenseAgreements.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { doc, serverTimestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useGetDocument } from '../hooks/useGetDocument';
import { useSetDocument } from '../hooks/useSetDocument';
import { Loader } from 'lucide-react';
import { LICENSE_AGREEMENTS_COLLECTION_PATH, USER_DOCUMENT_PATH } from '../utils/FirebaseUtils';

export default function LicenseAgreements({ firestore, auth, uid }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const licenseAgreementDocId = location.state?.licenseAgreementDocId;

  const licenseAgreementRef = doc(firestore, LICENSE_AGREEMENTS_COLLECTION_PATH, licenseAgreementDocId);
  const { data: licenseAgreementData, loading: isLoadingLicenseAgreement } = useGetDocument(licenseAgreementRef, {
    initialCall: !!licenseAgreementDocId,
  });
  const licenseAgreementText = licenseAgreementData?.['text'];

  const { setDocument: setUserDocument } = useSetDocument(doc(firestore, USER_DOCUMENT_PATH(uid)));

  async function writeLicenseAgreementDecision(accepted) {
    if (!uid) {
      return;
    }

    await setUserDocument(
      {
        legal: {
          license_agreement: {
            [licenseAgreementDocId]: {
              accepted: accepted,
              timestamp: serverTimestamp(),
            },
          },
        },
      },
      { merge: true },
    );
  }

  async function acceptLicenseAgreement() {
    await writeLicenseAgreementDecision(true);

    navigate('/');
    window.location.reload(false);
  }

  async function declineLicenseAgreement() {
    await writeLicenseAgreementDecision(false);

    signOut(auth);
    navigate('/login');
    window.location.reload(false);
  }

  return (
    <div className='license-agreement'>
      <div className='license-agreement-form'>
        <h1 className='license-agreement-title'>{t('license_agreement_title')}</h1>
        {isLoadingLicenseAgreement ? (
          <Loader size={20} className='animate-spin h-full mx-auto' />
        ) : (
          <div className='license-agreement-text' dangerouslySetInnerHTML={{ __html: licenseAgreementText ?? '' }} />
        )}
        <div className='license-agreement-buttons'>
          <button onClick={declineLicenseAgreement}>{t('license_agreement_decline')}</button>
          <button onClick={acceptLicenseAgreement}>{t('license_agreement_accept')}</button>
        </div>
      </div>
    </div>
  );
}
